import { CalendarOutlined } from '@ant-design/icons';
import AppButton from '@nxsys/components/AppButton';
import { AppConfirmationDelete } from '@nxsys/components/AppConfirmationDeleteModal';
import AppModalContainer from '@nxsys/components/AppModalContainer';
import AppsContent from '@nxsys/components/AppsContainer/AppsContent';
import AppTableContainer from '@nxsys/components/AppTableContainer';
import { TimesheetStatusEnum } from '@nxsys/constants/AppEnums';
import { defaultTheme } from '@nxsys/constants/defaultConfig';
import { useApproveTimesheet, useCancelTimesheet } from '@nxsys/hooks/api/timesheets';
import { useNavigateWithQuery } from '@nxsys/hooks/useNavigateWithQuery';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { AppState } from '@nxsys/store';
import { dashboardActions } from '@nxsys/store/slices/dashboard/reducer/dashboardSlice';
import { GetAgencyTimesheetListParams } from '@nxsys/store/slices/dashboard/types';
import { Icons } from '@nxsys/types/icons';
import { TimeSheetDataItem } from '@nxsys/types/models/timesheet';
import { showCurrencyByCountry } from '@nxsys/utils';
import { csvConfigExport } from '@nxsys/utils/export';
import { showMessage } from '@nxsys/utils/message';
import { formatMoney, formatMoneyExport } from '@nxsys/utils/number-helper';
import { Tag, Tooltip } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import { ExportToCsv } from 'export-to-csv';
import { isEmpty, toNumber } from 'lodash';
import moment from 'moment-timezone';
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import ChangeTaxPeriodModal from '../../timesheets/components/ChangeTaxPeriodModal';
import { resolveChangeTaxPeriodSelection } from '../../timesheets/listing/changeTaxPeriodSelection';

export enum AGENCY_TIMESHEET_MODAL_TYPE {
  TOTAL = 'TOTAL',
  AWAITING_APPROVAL = 'AWAITING_APPROVAL',
}

type Props = {
  isOpen: boolean;
  toggle: () => void;
  agencyId: string;
  type: AGENCY_TIMESHEET_MODAL_TYPE;
};

const AgencyTimesheetModal: React.FC<Props> = ({ isOpen, toggle, agencyId, type }) => {
  const { messages, formatMessage } = useTypedIntl();
  const navigate = useNavigateWithQuery();
  const { payrollCountry } = useSelector((state: AppState) => state.appStore);
  const dispatch = useDispatch();
  const { agencyTimesheetList } = useSelector((state: AppState) => state.dashboardStore);
  const { list, pagination, loading, queryParams } = agencyTimesheetList;

  const [selectedRows, setSelectedRows] = useState<TimeSheetDataItem[]>([]);
  const [taxPeriodModal, setTaxPeriodModal] = useState<{
    isOpen: boolean;
    timesheets: TimeSheetDataItem[];
  }>({
    isOpen: false,
    timesheets: [],
  });

  const toggleTaxPeriodModal = () => {
    setTaxPeriodModal((prev) => ({ ...prev, isOpen: !prev.isOpen }));
  };

  useEffect(() => {
    if (isOpen)
      handleFetchData({
        page: 0,
        size: 20,
      });
  }, [isOpen]);

  const handleFetchData = (params: Partial<GetAgencyTimesheetListParams>) => {
    const newParams = {
      ...params,
      isCurrentTaxPeriod: true,
      agencyIds: [agencyId],
      agencyRefs: [agencyId],
    };
    if (type === AGENCY_TIMESHEET_MODAL_TYPE.AWAITING_APPROVAL) {
      newParams.approveStatus = 'PENDING';
    }
    dispatch(dashboardActions.getAgencyTimesheetList(newParams));
  };

  const refetchTimesheetList = () => {
    handleFetchData({
      ...queryParams,
    });
  };

  const onChangePagination = (page: number, pageSize: number) => {
    const isPageSizeChanged = pageSize !== pagination.pageSize;
    const shouldResetPage = isPageSizeChanged && pagination.current > 0;
    handleFetchData({
      ...queryParams,
      page: shouldResetPage ? 0 : page - 1,
      size: pageSize,
    });
  };

  // NX-5237: the dashboard tile opens its own table rather than reusing
  // `getActionColumn`, so the Change Tax Period action had to be wired here
  // too. Same guard, same pure selection resolver as the listing screens.
  const handleOpenChangeTaxPeriod = useCallback((list: TimeSheetDataItem[]) => {
    const selection = resolveChangeTaxPeriodSelection(list);
    switch (selection.status) {
      case 'empty':
        showMessage({
          type: 'warning',
          content: messages['common.noSelected'],
        });
        return;
      case 'noneEligible':
        showMessage({
          type: 'warning',
          content: messages['timesheet.noValidTimesheetToChangeTaxPeriod'],
        });
        return;
      case 'mixedFrequency':
        showMessage({
          type: 'warning',
          content: messages['timesheet.changeTaxPeriodMixedFrequency'],
        });
        return;
      case 'ok':
        setTaxPeriodModal({ isOpen: true, timesheets: selection.selected });
        return;
    }
  }, []);

  const columns: ColumnsType<TimeSheetDataItem> = [
    {
      key: 'action',
      title: 'Action',
      fixed: 'left',
      align: 'left',
      render: (record) => {
        const ableCancelTs = !(
          record?.approveStatus === TimesheetStatusEnum.CANCEL ||
          record?.newDraftPayroll ||
          record?.newPayroll
        );

        return (
          <ul className="flex items-center justify-start gap-x-4">
            <li
              onClick={() =>
                navigate(`/timesheets-agency/${record.id}`, {
                  query: {
                    from: '/timesheets-agency',
                  },
                })
              }
            >
              <Tooltip title={messages['common.view']}>
                <span>
                  <Icons.IconEye />
                </span>
              </Tooltip>
            </li>
            {record.file && (
              <li onClick={() => window.open(record.file, '_blank')}>
                <Tooltip title={messages['common.download']}>
                  <span className="w-[20px] h-[20px]">
                    <Icons.IconDownload />
                  </span>
                </Tooltip>
              </li>
            )}
            {ableCancelTs && (
              <li onClick={() => handleCancel([record])}>
                <Tooltip title={messages['timesheet.cancel']}>
                  <span>
                    <Icons.IconClose
                      color={defaultTheme.theme.palette.red[500]}
                    />
                  </span>
                </Tooltip>
              </li>
            )}
            {ableCancelTs && (
              <li onClick={() => handleOpenChangeTaxPeriod([record])}>
                <Tooltip title={messages['timesheet.changeTaxPeriod']}>
                  <span>
                    <CalendarOutlined
                      style={{ color: defaultTheme.theme.palette.primary.main }}
                    />
                  </span>
                </Tooltip>
              </li>
            )}
            {record?.isPAL && (
              <li>
                <Tag color="warning" className="ml-1">
                  PAL
                </Tag>
              </li>
            )}
          </ul>
        );
      },
      width: 150,
    },
    {
      key: 'status',
      title: 'Status',
      align: 'center',
      dataIndex: 'approveStatus',
      render: (value) => <span>{value === 'PENDING' ? 'Submitted' : ''}</span>,
    },
    {
      key: 'forename',
      title: 'Forename',
      dataIndex: 'subcontractorRef',
      render: (value) => <span>{value?.forename}</span>,
    },
    {
      key: 'surname',
      title: 'Surname',
      dataIndex: 'subcontractorRef',
      render: (value) => <span>{value?.surname}</span>,
    },
    {
      key: 'date',
      title: 'Date',
      align: 'right',
      dataIndex: 'invoicedDate',
      render: (props) => (
        <span>
          {props ? moment(props).tz('Europe/London').format('DD/MM/YYYY') : '_'}
        </span>
      ),
    },
    {
      key: 'agencyRef',
      title: 'Agency Ref',
      dataIndex: 'subcontractorRef',
      render: (value) =>
        `${value?.primaryAgency?.agencyPrefix || '___'}/${
          value?.agencyRef || value?.systemId || '_'
        }`,
      width: 240,
    },
    {
      key: 'id',
      title: 'Timesheet ID',
      dataIndex: 'id',
      sorter: true,
      sortDirections: ['descend', 'ascend'],
      width: 240,
      render: (value) => <span>{value || '_'}</span>,
    },
    {
      key: 'id',
      title: 'Employee ID',
      dataIndex: 'subcontractorRef',
      sorter: true,
      sortDirections: ['descend', 'ascend'],
      width: 240,
      render: (props) => <span>{props?.id}</span>,
    },
    {
      key: 'candidateName',
      title: 'Candidate Name',
      dataIndex: 'subcontractorRef',
      sorter: true,
      sortDirections: ['descend', 'ascend'],
      width: 240,
      render: (props) => (
        <span>
          {props?.fullName ||
            `${props?.forename || ''} ${props?.surname || ''}`}
        </span>
      ),
    },
    {
      key: 'batch',
      title: 'Batch #',
      dataIndex: 'batch',
    },
    {
      key: 'endDate',
      title: 'Week End Date',
      dataIndex: 'endDate',
      align: 'right',
      sorter: true,
      width: 180,
      sortDirections: ['descend', 'ascend'],
      render: (props) => (
        <span>
          {props ? moment(props).tz('Europe/London').format('DD/MM/YYYY') : '_'}
        </span>
      ),
    },
    {
      key: 'taxPeriod',
      title: 'Tax Period',
      render: (record) => (
        <span>{`${record?.taxPeriod || ''}/${
          record?.taxYear || record?.currentYear || ''
        }`}</span>
      ),
      align: 'right',
    },
    {
      key: 'totalNet',
      title: 'Net',
      dataIndex: 'totalNet',
      align: 'right',
      sorter: true,
      sortDirections: ['descend', 'ascend'],
      render: (value) => (
        <span>{formatMoney(value, showCurrencyByCountry(payrollCountry))}</span>
      ),
    },
    {
      key: 'totalVat',
      title: 'VAT',
      align: 'right',
      sorter: true,
      dataIndex: 'totalVat',
      sortDirections: ['descend', 'ascend'],
      render: (value) => (
        <span>{formatMoney(value, showCurrencyByCountry(payrollCountry))}</span>
      ),
    },
    {
      key: 'totalPrices',
      title: 'Total',
      dataIndex: 'totalPrices',
      align: 'right',
      sorter: true,
      sortDirections: ['descend', 'ascend'],
      render: (value) => (
        <span>
          {formatMoney(value || 0, showCurrencyByCountry(payrollCountry))}
        </span>
      ),
    },
  ];

  const { run: cancelTimesheet } =
    useCancelTimesheet();
  const handleCancel = (list: TimeSheetDataItem[]) => {
    const listToCancelId = list
      .filter(
        (s) =>
          !s.newDraftPayrollId &&
          !s.newPayrollId &&
          s.approveStatus !== 'CANCEL',
      )
      .map((e) => e.id);

    if (!isEmpty(listToCancelId)) {
      AppConfirmationDelete.open({
        title: messages['timesheet.cancel'],
        paragraph: formatMessage(
          { id: 'timesheet.cancelConfirm' },
          {
            timesheetList: (
              <span style={{ color: defaultTheme.theme.palette.blue[500] }}>
                {listToCancelId.join(', ')}
              </span>
            ),
          },
        ),
        onConfirm: async () => {
          await cancelTimesheet(listToCancelId);
          showMessage({
            type: 'success',
            content: messages['timesheet.cancelSuccess'],
          });
          refetchTimesheetList();
        },
      });
    } else {
      const canceledIndex = list.findIndex(
        (e) => e.approveStatus === 'CANCEL',
      );
      if (canceledIndex >= 0) {
        showMessage(
          {
            type: 'error',
            content: `Timesheet "${list[canceledIndex].id}" is already canceled.`,
          }
        );
      }
      showMessage(
        {
          type: 'error',
          content: 'These timesheets were already processed, can be reversed only.',
        }
      );
    }


  };
  const handleCancelSelected = () => {
    if (selectedRows.length === 0) {
      showMessage({
        type: 'warning',
        content: messages['common.noSelected'],
      });
      return;
    }
    handleCancel(selectedRows);
  };

  const handleExport = (data: TimeSheetDataItem[]) => {
    const dataExport = data.map((item) => ({
      id: item?.id,
      agencyName: item?.agencyRef?.name || '',
      name: `${item?.subcontractorRef?.forename || ''} ${item?.subcontractorRef?.surname || ''
        }`,
      endDate: item?.endDate
        ? moment(item?.endDate).tz('Europe/London').format('DD/MM/YYYY')
        : '',
      batch: item?.batch || '',
      taxPeriod: item?.taxPeriod || '',
      taxYear: item?.taxYear || item?.currentYear || '',
      totalNet: formatMoneyExport(toNumber(item?.totalNet) || 0, showCurrencyByCountry(payrollCountry)),
      totalVat: formatMoneyExport(toNumber(item?.totalVat) || 0, showCurrencyByCountry(payrollCountry)),
      totalPrices: formatMoneyExport(toNumber(item?.totalPrices) || 0, showCurrencyByCountry(payrollCountry)),
      receipt: formatMoneyExport(toNumber(item?.receipt) || 0, showCurrencyByCountry(payrollCountry)),
      outstanding: formatMoneyExport(toNumber(item?.outstanding) || 0, showCurrencyByCountry(payrollCountry)),
      invoiceNo: item?.invoiceNo || '',
    }));

    const headers = [
      'Timesheet ID',
      'Agency Name',
      'Employee Name',
      'Week End Date',
      'Batch #',
      'Tax Period',
      'Tax Year',
      'Net',
      'VAT',
      'Total',
      'Receipt',
      'Outstanding',
      'Sales INV Ref',
    ];

    const exportName = csvConfigExport(`Timesheet-export`, headers);
    const csvExporter = new ExportToCsv(exportName);
    csvExporter.generateCsv(dataExport);
  };
  const handleExportSelected = () => {
    if (selectedRows.length === 0) {
      showMessage({
        type: 'warning',
        content: messages['common.noSelected'],
      });
      return;
    }
    handleExport(selectedRows);
  };

  const { run: approveTimesheet, loading: isApproveTimesheetPending } =
    useApproveTimesheet();
  const handleApprove = useCallback((list: TimeSheetDataItem[]) => {
    const ids = list.map((item) => item.id);
    approveTimesheet({ ids }).then(() => {
      showMessage({
        type: 'success',
        content: messages['timesheet.approveSuccess'],
      });
      refetchTimesheetList();
      setSelectedRows([]);
    });
  }, []);

  const handleApproveSelected = useCallback(() => {
    if (selectedRows.length === 0) {
      showMessage({
        type: 'warning',
        content: messages['common.noSelected'],
      });
      return;
    }
    handleApprove(selectedRows);
  }, [selectedRows]);

  return (
    <AppModalContainer
      title={
        <div className="flex items-center justify-between w-full gap-x-[8px]">
          <div>{type === AGENCY_TIMESHEET_MODAL_TYPE.TOTAL ? 'Total timesheets' : 'Awaiting approval'}</div>
          <div className="flex justify-end gap-x-2">
            <AppButton variant="outlined" name="Cancel" onClick={handleCancelSelected} />
            {type === AGENCY_TIMESHEET_MODAL_TYPE.TOTAL && (
              <AppButton name="Export" onClick={handleExportSelected} />
            )}
            {type === AGENCY_TIMESHEET_MODAL_TYPE.AWAITING_APPROVAL && (
              <AppButton name="Approve" onClick={handleApproveSelected} loading={isApproveTimesheetPending} />
            )}
          </div>
        </div>
      }
      isOpen={isOpen}
      toggle={toggle}
      hiddenFooter
      className="max-sm:w-[100vw]"
      width={1200}
      minHeight="0"
      noPaddingBody
      titleFullWidth
    >
      <AppsContent>
        <AppTableContainer
          rowKey="id"
          data={list ?? []}
          loading={loading}
          page={pagination.current}
          total={pagination.totalDocs}
          pageSize={pagination.pageSize}
          scroll={{ x: 3000 }}
          columns={columns}
          onChangePagination={onChangePagination}
          rowSelection={{
            type: 'checkbox',
            onChange: (_: React.Key[], selectedRows: TimeSheetDataItem[]) => {
              setSelectedRows(selectedRows);
            },
          }}
        />
      </AppsContent>
      <ChangeTaxPeriodModal
        isOpen={taxPeriodModal.isOpen}
        toggle={toggleTaxPeriodModal}
        timesheets={taxPeriodModal.timesheets}
        onSuccess={() => {
          refetchTimesheetList();
          setSelectedRows([]);
        }}
      />
    </AppModalContainer>
  );
};

export default AgencyTimesheetModal;
